import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    FiBell,
    FiTarget,
    FiCheck,
    FiCheckCircle,
    FiClock,
    FiSearch,
    FiRefreshCw,
    FiInbox,
    FiCalendar,
    FiBriefcase,
    FiUser
} from 'react-icons/fi';
import './../Styles/Notifications.css';

const apiUrl = process.env.REACT_APP_API_URL || '';

const formatTimeAgo = (dateInput) => {
    if (!dateInput) return '';
    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return '';
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
        hour: '2-digit',
        minute: '2-digit'
    });
};

const Notifications = ({ managerId, isMainManager = false, onUnreadCountChange }) => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [filter, setFilter] = useState('all'); // 'all' | 'unread'
    const [searchQuery, setSearchQuery] = useState('');
    const [actionLoadingId, setActionLoadingId] = useState(null);

    const activeManagerId = managerId || localStorage.getItem('employeeId');
    const isMain = Boolean(isMainManager || localStorage.getItem('isMainManager') === '1');

    // Fetch notifications and unread count
    const fetchNotificationsData = useCallback(async (isSilent = false) => {
        if (!activeManagerId) return;

        if (!isSilent) {
            setRefreshing(true);
        }

        try {
            const [notifsRes, countRes] = await Promise.all([
                axios.get(`${apiUrl}/api/notifications/${activeManagerId}?isMainManager=${isMain ? 1 : 0}`),
                axios.get(`${apiUrl}/api/notifications/unread/${activeManagerId}?isMainManager=${isMain ? 1 : 0}`)
            ]);

            const list = Array.isArray(notifsRes.data) ? notifsRes.data : [];
            const count = countRes.data?.unreadCount ?? 0;

            setNotifications(list);
            setUnreadCount(count);

            if (onUnreadCountChange) {
                onUnreadCountChange(count);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [activeManagerId, isMain, onUnreadCountChange]);

    // Initial load and periodic polling
    useEffect(() => {
        fetchNotificationsData(false);

        // Poll every 15 seconds for fresh notifications
        const interval = setInterval(() => {
            fetchNotificationsData(true);
        }, 15000);

        return () => clearInterval(interval);
    }, [fetchNotificationsData]);

    // Mark single notification as read
    const handleMarkAsRead = async (e, id) => {
        e.stopPropagation();
        if (!id || actionLoadingId === id) return;

        setActionLoadingId(id);
        try {
            await axios.post(`${apiUrl}/api/notifications/mark-read/${id}`);

            setNotifications(prev =>
                prev.map(item =>
                    (item._id === id || item.id === id)
                        ? { ...item, read: true, is_read: true }
                        : item
                )
            );

            setUnreadCount(prev => {
                const nextCount = Math.max(0, prev - 1);
                if (onUnreadCountChange) onUnreadCountChange(nextCount);
                return nextCount;
            });
        } catch (err) {
            console.error('Failed to mark notification as read:', err);
        } finally {
            setActionLoadingId(null);
        }
    };

    // Mark all notifications as read
    const handleMarkAllAsRead = async () => {
        if (unreadCount === 0 || refreshing) return;

        setRefreshing(true);
        try {
            await axios.post(`${apiUrl}/api/notifications/mark-all-read/${activeManagerId}?isMainManager=${isMain ? 1 : 0}`);

            setNotifications(prev =>
                prev.map(item => ({ ...item, read: true, is_read: true }))
            );

            setUnreadCount(0);
            if (onUnreadCountChange) onUnreadCountChange(0);
        } catch (err) {
            console.error('Failed to mark all notifications as read:', err);
        } finally {
            setRefreshing(false);
        }
    };

    // Filter and search logic
    const filteredNotifications = notifications.filter(item => {
        const isRead = Boolean(item.read || item.is_read);

        if (filter === 'unread' && isRead) {
            return false;
        }

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            const matchesMessage = (item.message || '').toLowerCase().includes(query);
            const matchesEmpName = (item.employee_name || '').toLowerCase().includes(query);
            const matchesEmpId = (item.employee_id || '').toLowerCase().includes(query);
            const matchesProject = (item.project_name || '').toLowerCase().includes(query);
            const matchesQuarter = (item.quarter || '').toLowerCase().includes(query);
            return matchesMessage || matchesEmpName || matchesEmpId || matchesProject || matchesQuarter;
        }

        return true;
    });

    return (
        <div className="notifications-dashboard">
            {/* Header Section */}
            <div className="notifications-header">
                <div className="notifications-title-area">
                    <div className="notifications-header-badge">
                        <FiBell className="notifications-bell-icon" />
                    </div>
                    <div>
                        <h2>Updates & Goal Notifications</h2>
                        <p className="notifications-subtitle">
                            Real-time updates when employees submit goals for review.
                        </p>
                    </div>
                </div>

                <div className="notifications-actions">
                    {unreadCount > 0 && (
                        <button
                            type="button"
                            className="notif-btn notif-btn-secondary"
                            onClick={handleMarkAllAsRead}
                            disabled={refreshing}
                            title="Mark all updates as read"
                        >
                            <FiCheckCircle size={15} />
                            <span>Mark all as read</span>
                        </button>
                    )}

                    <button
                        type="button"
                        className={`notif-btn notif-btn-icon ${refreshing ? 'spinning' : ''}`}
                        onClick={() => fetchNotificationsData(false)}
                        disabled={refreshing}
                        title="Refresh updates"
                        aria-label="Refresh updates"
                    >
                        <FiRefreshCw size={15} />
                    </button>
                </div>
            </div>

            {/* Filter and Search Bar */}
            <div className="notifications-toolbar">
                <div className="notifications-filters">
                    <button
                        type="button"
                        className={`filter-chip ${filter === 'all' ? 'active' : ''}`}
                        onClick={() => setFilter('all')}
                    >
                        All Updates <span className="chip-count">{notifications.length}</span>
                    </button>
                    <button
                        type="button"
                        className={`filter-chip ${filter === 'unread' ? 'active' : ''}`}
                        onClick={() => setFilter('unread')}
                    >
                        Unread <span className="chip-count chip-count-unread">{unreadCount}</span>
                    </button>
                </div>

                <div className="notifications-search-wrapper">
                    <FiSearch className="search-icon" />
                    <input
                        type="text"
                        className="notifications-search-input"
                        placeholder="Search by employee, ID, project, or quarter..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="search-clear-btn"
                            onClick={() => setSearchQuery('')}
                            aria-label="Clear search"
                        >
                            &times;
                        </button>
                    )}
                </div>
            </div>

            {/* Content List */}
            <div className="notifications-content">
                {loading ? (
                    <div className="notifications-loading">
                        <div className="notif-spinner"></div>
                        <p>Loading updates...</p>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="notifications-empty">
                        <div className="empty-icon-wrap">
                            <FiInbox size={42} />
                        </div>
                        <h3>{filter === 'unread' ? 'No unread updates' : 'No updates found'}</h3>
                        <p>
                            {searchQuery
                                ? 'No notifications match your current search.'
                                : filter === 'unread'
                                ? 'You are all caught up! There are no unread goal submissions.'
                                : 'When an employee submits their goals for review, updates will automatically appear here.'}
                        </p>
                    </div>
                ) : (
                    <div className="notifications-feed">
                        {filteredNotifications.map((notif) => {
                            const isRead = Boolean(notif.read || notif.is_read);
                            const id = notif._id || notif.id;

                            return (
                                <div
                                    key={id}
                                    className={`notification-card ${!isRead ? 'unread' : 'read'}`}
                                >
                                    <div className="notif-icon-bubble">
                                        <FiTarget size={18} />
                                    </div>

                                    <div className="notif-details">
                                        <div className="notif-meta-row">
                                            <div className="notif-tags">
                                                {notif.employee_name && (
                                                    <span className="notif-tag notif-tag-employee">
                                                        <FiUser size={12} />
                                                        {notif.employee_name}
                                                    </span>
                                                )}
                                                {notif.employee_id && (
                                                    <span className="notif-tag notif-tag-id">
                                                        ID: {notif.employee_id}
                                                    </span>
                                                )}
                                                {notif.quarter && (
                                                    <span className="notif-tag notif-tag-quarter">
                                                        <FiCalendar size={12} />
                                                        {notif.quarter}
                                                    </span>
                                                )}
                                                {notif.project_name && (
                                                    <span className="notif-tag notif-tag-project">
                                                        <FiBriefcase size={12} />
                                                        {notif.project_name}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="notif-time" title={new Date(notif.created_at || notif.timestamp).toLocaleString()}>
                                                <FiClock size={12} />
                                                <span>{formatTimeAgo(notif.created_at || notif.timestamp)}</span>
                                            </div>
                                        </div>

                                        <p className="notif-message">
                                            {notif.message}
                                        </p>
                                    </div>

                                    <div className="notif-card-actions">
                                        {!isRead ? (
                                            <button
                                                type="button"
                                                className="btn-mark-read"
                                                onClick={(e) => handleMarkAsRead(e, id)}
                                                disabled={actionLoadingId === id}
                                                title="Mark as read"
                                            >
                                                <FiCheck size={14} />
                                                <span>Mark as read</span>
                                            </button>
                                        ) : (
                                            <span className="status-read-indicator" title="Read">
                                                <FiCheckCircle size={15} />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Notifications;
